import {
	useMutation,
	UseMutationResult,
	useQuery,
	UseQueryResult,
} from '@tanstack/react-query'

import { PaginatedResponse } from '@core/api/dtos/common/types'
import { UpdateScheduleRequestDTO } from '@core/api/dtos/request/schedule/update'
import { Group } from '@core/api/entities/group'
import { Schedule } from '@core/api/entities/schedule'
import { Zone } from '@core/api/entities/zone/zone'
import { GroupMapper } from '@core/api/mappers/groupMapper'
import { ScheduleMapper } from '@core/api/mappers/scheduleMapper'
import { ZoneMapper } from '@core/api/mappers/zone/zoneMapper'
import scheduleService from '@core/api/services/scheduleService'

import { DEFAULT_PARAMS } from './common'

export const useSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Schedule>,
	Error
> => {
	const QUERY_KEY = [
		'schedules',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService.getSchedules(config).then(({ data: response }) => {
				return {
					data: ScheduleMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

export const useAssignedSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Schedule>,
	Error
> => {
	const QUERY_KEY = [
		'schedules-assigned',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getAssignedSchedules(config)
				.then(({ data: response }) => {
					return {
						data: ScheduleMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

export const useInUseSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Schedule>,
	Error
> => {
	const QUERY_KEY = [
		'schedules-in-use',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService.getInUseSchedules(config).then(({ data: response }) => {
				return {
					data: ScheduleMapper.toEntities(response.data),
					meta: response.meta,
				}
			}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

export const useSchedule = ({
	scheduleId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Schedule, Error> => {
	const QUERY_KEY = [
		'schedule',
		scheduleId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getSchedule(scheduleId, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntity(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

export const useScheduleDetails = ({
	scheduleId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Schedule, Error> => {
	const QUERY_KEY = [
		'schedule-details',
		scheduleId,
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getScheduleDetails(scheduleId, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntity(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		enabled: !!scheduleId,
		...options,
	})
}

export const useCreateSchedule = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Schedule,
	Error,
	UpdateScheduleRequestDTO,
	any
> => {
	return useMutation<Schedule, Error, UpdateScheduleRequestDTO, any>({
		mutationFn: (data: UpdateScheduleRequestDTO) =>
			scheduleService
				.createSchedule(data, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntity(response.data)
				}),
		...options,
	})
}

export const useUpdateSchedule = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Schedule,
	Error,
	{ scheduleId: string; data: UpdateScheduleRequestDTO },
	any
> => {
	return useMutation<
		Schedule,
		Error,
		{ scheduleId: string; data: UpdateScheduleRequestDTO },
		any
	>({
		mutationFn: ({
			scheduleId,
			data,
		}: {
			scheduleId: string
			data: UpdateScheduleRequestDTO
		}) =>
			scheduleService
				.updateSchedule(scheduleId, data, config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntity(response.data)
				}),
		...options,
	})
}

export const useDeleteSchedule = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<void, Error, string, any> => {
	return useMutation<void, Error, string, any>({
		mutationFn: (scheduleId: string) =>
			scheduleService.deleteSchedule(scheduleId, config).then(() => {
				return
			}),
		...options,
	})
}

export const useToggleScheduleFavorite = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	void,
	Error,
	{ scheduleId: string; favorite: boolean },
	any
> => {
	return useMutation<
		void,
		Error,
		{ scheduleId: string; favorite: boolean },
		any
	>({
		mutationFn: ({
			scheduleId,
			favorite,
		}: {
			scheduleId: string
			favorite: boolean
		}) =>
			scheduleService
				.toggleScheduleFavorite(scheduleId, favorite, config)
				.then(() => {
					return
				}),
		...options,
	})
}

export const useScheduleGroups = ({
	scheduleId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Group[], Error> => {
	const QUERY_KEY = ['schedule-groups', scheduleId, config?.params]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getScheduleGroups(scheduleId, config)
				.then(({ data: response }) => {
					return GroupMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		enabled: !!scheduleId,
		...options,
	})
}

export const useDuplicateSchedule = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseMutationResult<
	Schedule,
	Error,
	{ sourceScheduleId: string; newTitle: string },
	any
> => {
	return useMutation<
		Schedule,
		Error,
		{ sourceScheduleId: string; newTitle: string },
		any
	>({
		mutationFn: async ({
			sourceScheduleId,
			newTitle,
		}: {
			sourceScheduleId: string
			newTitle: string
		}) => {
			const sourceResponse = await scheduleService.getScheduleDetails(
				sourceScheduleId,
				config
			)

			const sourceData = sourceResponse.data?.data
			if (!sourceData) {
				throw new Error('Schedule details could not be loaded')
			}

			const timeslots = sourceData.timeslots?.data ?? []

			const getDescription = () => {
				if (!sourceData.description) {
					return { 'en-US': '' }
				}
				if (typeof sourceData.description === 'object') {
					return sourceData.description
				}
				return { 'en-US': sourceData.description }
			}

			const duplicateData: UpdateScheduleRequestDTO = {
				title: {
					'en-US': newTitle,
				},
				description: getDescription(),
				timeslots: timeslots.map((slot: any) => ({
					name: slot.name,
					type: slot.type || 'regular',
					priority: slot.priority ?? 0,
					vevent: slot.vevent ?? '',
					timeslot_contents: (slot.timeslot_contents?.data ?? []).map(
						(content: any) => ({
							type: content.type,
							volume: content.metadata?.volume ?? 100,
							...(content.metadata?.min_percentage !== undefined
								? { min_percentage: content.metadata.min_percentage }
								: {}),
							...(content.metadata?.max_percentage !== undefined
								? { max_percentage: content.metadata.max_percentage }
								: {}),
							playlists: (content.playlists?.data ?? []).map(
								(pl: { id: number; percentage?: number }) => ({
									id: pl.id,
									percentage: pl.percentage ?? 100,
								})
							),
						})
					),
				})),
			}

			const response = await scheduleService.createSchedule(
				duplicateData,
				config
			)

			const responseData = response.data
			return ScheduleMapper.toEntity(responseData)
		},
		...options,
	})
}

export const useRecommendedSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Schedule[], Error> => {
	const QUERY_KEY = [
		'recommended-schedules',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getRecommendedSchedules(config)
				.then(({ data: response }) => {
					return ScheduleMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

export const useFavoriteSchedules = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<
	PaginatedResponse<Schedule>,
	Error
> => {
	const QUERY_KEY = [
		'schedules-favorites',
		{
			...config?.params,
		},
	]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getFavoriteSchedules(config)
				.then(({ data: response }) => {
					return {
						data: ScheduleMapper.toEntities(response.data),
						meta: response.meta,
					}
				}),
		cacheTime: 0,
		staleTime: 0,
		...options,
	})
}

/**
 * Get zones a schedule is assigned to
 */
export const useScheduleZones = ({
	scheduleId,
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Zone[], Error> => {
	const QUERY_KEY = ['schedule-zones', scheduleId, config?.params]

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			scheduleService
				.getScheduleZones(scheduleId, config)
				.then(({ data: response }) => {
					return ZoneMapper.toEntities(response.data)
				}),
		cacheTime: 0,
		staleTime: 0,
		enabled: !!scheduleId,
		...options,
	})
}
